// Client portal shell — other Business UI handoff (P58, 2026-10-07): the
// same frame as the team's apps, for the customer. A 216px (13.5rem) white sidebar
// ("Your company": Dashboard, Orders, Quotes & presentations, Invoices,
// Messages, Brand assets, New
// project, Account; the current page in black) and a 68px top bar with who
// is signed in. Server-filtered data only — nothing here hides fields with CSS.
//
// Full width + large type (CEO 2026-10-19, the Flow treatment of 2026-10-14):
// <main> is THE page container — it uses the width beside the sidebar (a
// 2,400px cap only for ultra-wide monitors) with generous side padding that
// grows with the screen; pages don't add their own centred max-w wrappers.
// `.portal-shell` switches on the larger type in index.css (wide screens
// only); sizes here are rem so the sidebar and top bar grow with it. Phones
// keep the 16px gutters and the slide-in menu.
//
// Company stores (2026-10-12): a store employee (store_member, no client
// role) sees ONLY the store links. A client who belongs to a store also gets
// a "Company store" group (plus People / Approvals / Settings when they are
// its admin) — shown only once GET /portal/store answers with a store; no
// store, or stores not switched on yet, and the group simply isn't there.
// S4 shop: everyone in a store gets Store, Cart (pieces in it) and Your
// orders; an admin also gets Approvals (how many are waiting), People and
// Settings. The Approvals count is orders waiting for approval PLUS approved
// company-card orders waiting for the admin to pay (UX review 2026-10-12).
import { useState, type ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthProvider";
import { fetchMyStore, isStoreOnlyUser, MY_STORE_KEY } from "@/lib/portal-store";
import { cartPieces, useStoreCart } from "@/lib/store-cart";
import { fetchAdminOrders, fetchAdminOrdersToPay, STORE_ADMIN_TO_PAY_KEY, STORE_PENDING_KEY } from "@/lib/store-shop";

type NavItem = { to: string; label: string; end?: boolean; count?: number; countLabel?: string };

const STORE_ITEMS: NavItem[] = [
  { to: "/store", label: "Store", end: true },
  { to: "/store/cart", label: "Cart" },
  { to: "/store/orders", label: "Your orders" },
];

const STORE_ADMIN_ITEMS: NavItem[] = [
  { to: "/store/admin/approvals", label: "Approvals" },
  { to: "/store/admin/people", label: "People" },
  { to: "/store/admin/settings", label: "Settings" },
];

const NAV_ITEMS: Array<{ to: string; label: string }> = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/orders", label: "Orders" },
  { to: "/quotes", label: "Quotes & presentations" },
  { to: "/invoices", label: "Invoices" },
  { to: "/messages", label: "Messages" },
  { to: "/brand-assets", label: "Brand assets" },
  { to: "/request-project", label: "New project" },
  { to: "/account", label: "Account" },
];

export function PortalLayout({ children }: { children: ReactNode }) {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const storeOnly = isStoreOnlyUser(user);
  const { data: myStore } = useQuery({
    queryKey: MY_STORE_KEY,
    queryFn: fetchMyStore,
    enabled: !!user,
    retry: false,
  });
  const isStoreAdmin = myStore?.role === "admin";
  const { cart } = useStoreCart(myStore?.store.id, user?.id);
  const pieces = cartPieces(cart);
  const { data: pending } = useQuery({
    queryKey: STORE_PENDING_KEY,
    queryFn: () => fetchAdminOrders("pending_approval"),
    enabled: !!user && isStoreAdmin,
    staleTime: 30_000,
    retry: false,
  });
  const { data: toPay } = useQuery({
    queryKey: STORE_ADMIN_TO_PAY_KEY,
    queryFn: fetchAdminOrdersToPay,
    enabled: !!user && isStoreAdmin,
    staleTime: 30_000,
    retry: false,
  });
  const waiting = (pending?.length ?? 0) + (toPay?.length ?? 0);
  // Employee: the whole store menu, always. Client: the store menu (and the
  // admin pages if they run it) only when they actually belong to a store.
  const counted = (item: NavItem): NavItem =>
    item.to === "/store/cart"
      ? { ...item, count: pieces, countLabel: `${pieces} ${pieces === 1 ? "piece" : "pieces"} in your cart` }
      : item.to === "/store/admin/approvals"
        ? { ...item, count: waiting, countLabel: `${waiting} waiting for you` }
        : item;
  const storeItems: NavItem[] = (storeOnly || myStore)
    ? [...STORE_ITEMS, ...(isStoreAdmin ? STORE_ADMIN_ITEMS : [])].map(counted)
    : [];

  const navLink = (item: NavItem) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={() => setOpen(false)}
      className={({ isActive }) =>
        "flex min-h-[2.5rem] items-center rounded px-3 text-sm transition-colors " +
        (isActive ? "bg-ink text-white" : "text-ink hover:bg-canvas")
      }
    >
      <span className="flex-1">{item.label}</span>
      {item.count ? (
        <span className="ml-2 min-w-[1.375rem] rounded-full bg-rust-500 px-1.5 text-center text-[0.6875rem] font-semibold leading-5 text-white" aria-label={item.countLabel}>
          {item.count > 99 ? "99+" : item.count}
        </span>
      ) : null}
    </NavLink>
  );

  return (
    <div className="portal-shell flex min-h-screen bg-canvas">
      {open && <div className="fixed inset-0 z-30 bg-ink/30 md:hidden" onClick={() => setOpen(false)} aria-hidden />}
      <aside
        className={
          "fixed inset-y-0 left-0 z-40 flex w-[13.5rem] flex-col border-r border-rule bg-white px-[1.125rem] py-[1.875rem] transition-transform md:static md:translate-x-0 " +
          (open ? "translate-x-0" : "-translate-x-full")
        }
      >
        <div className="flex items-start justify-between px-3">
          <Link to={storeOnly ? "/store" : "/dashboard"} aria-label="other — client portal home">
            <img src="/brand/other-primary-lockup-black.svg" width={106} height={44} className="h-auto w-[6.625rem]" alt="other — merch. paper. gifts." draggable={false} />
          </Link>
          <button type="button" className="md:hidden" aria-label="Close menu" onClick={() => setOpen(false)}>
            <span aria-hidden className="text-xl leading-none">×</span>
          </button>
        </div>
        {!storeOnly && (
          <>
            <p className="mx-3 mb-2 mt-8 text-[0.6875rem] uppercase tracking-[0.12em] text-[#777]">Your company</p>
            <nav aria-label="Portal" className="flex flex-col gap-[0.1875rem]">
              {NAV_ITEMS.map((item) => navLink(item))}
            </nav>
          </>
        )}
        {storeItems.length > 0 && (
          <>
            <p className="mx-3 mb-2 mt-8 text-[0.6875rem] uppercase tracking-[0.12em] text-[#777]">
              {myStore?.store.name ?? "Company store"}
            </p>
            <nav aria-label="Company store" className="flex flex-col gap-[0.1875rem]">
              {storeItems.map((item) => navLink(item))}
            </nav>
          </>
        )}
        <p className="mt-auto px-3 pt-8 text-xs text-muted">
          <span className="block font-medium text-ink">Good work. Together.</span>
          other · client portal
        </p>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-[4.25rem] items-center gap-5 border-b border-rule bg-white px-4 md:px-8 xl:px-12 2xl:px-16">
          <button type="button" className="md:hidden" aria-label="Open menu" aria-expanded={open} onClick={() => setOpen(true)}>
            <span aria-hidden className="text-xl leading-none">☰</span>
          </button>
          <div className="ml-auto flex items-center gap-4">
            {user && (
              <span className="hidden text-right text-xs sm:block">
                <b className="block font-semibold text-ink">{user.name}</b>
                <span className="text-muted">{user.email}</span>
              </span>
            )}
            <button
              type="button"
              onClick={() => void logout()}
              className="inline-flex min-h-[2.5rem] items-center gap-1.5 rounded border border-rule bg-white px-3.5 text-[0.8125rem] text-ink hover:bg-canvas"
            >
              Sign out
            </button>
          </div>
        </header>
        <main className="mx-auto w-full max-w-[2400px] flex-1 px-4 py-6 sm:px-8 sm:py-8 xl:px-12 xl:py-10 2xl:px-16">{children}</main>
      </div>
    </div>
  );
}
